import { type ChangeEvent, type RefObject, useEffect, useRef, useState } from "react"
import { Grid, GridItem, Stack, TextField } from "@octopus-energy/coral/atoms"
import { type FormikTextFieldProps } from "@octopus-energy/coral/formik"
import { useFormikContext } from "formik"
import { useTranslations } from "next-intl"
import { z } from "zod"
import { FormikErrorMessage } from "../FormikErrorMessage"

const dateValidationSchema = z.object({
    day: z
        .string()
        .refine((value) => !isNaN(Number(value)) && Number(value) >= 1 && Number(value) <= 31, {
            error: "Jour invalide",
        }),
    month: z
        .string()
        .refine((value) => !isNaN(Number(value)) && Number(value) >= 1 && Number(value) <= 12, {
            error: "Mois invalide",
        }),
    year: z
        .string()
        .refine(
            (value) =>
                !isNaN(Number(value)) &&
                Number(value) >= 1900 &&
                Number(value) <= new Date().getFullYear(),
            {
                error: "Année invalide",
            }
        ),
})

enum DateField {
    Day = "day",
    Month = "month",
    Year = "year",
}
type DateFieldNames = `${DateField}`
type FormikDateFieldType = Record<DateFieldNames, string>

const format = {
    inputDate: (date: Date | null) => ({
        day: date ? String(date.getDate()) : "",
        month: date ? String(date.getMonth() + 1) : "",
        year: date ? String(date.getFullYear()) : "",
    }),
    outputDate: (date: FormikDateFieldType) =>
        new Date(Number(date.year), Number(date.month) - 1, Number(date.day)),
}

type FormikDateFieldProps = Pick<FormikTextFieldProps, "name" | "disabled"> & {
    value: Date | null
    maxDate?: Date | null
}

export function FormikDateField(props: FormikDateFieldProps) {
    const t = useTranslations("FormikDateField")

    const monthRef = useRef<HTMLInputElement>(null)
    const yearRef = useRef<HTMLInputElement>(null)

    const { submitCount, setFieldValue } = useFormikContext()
    const { name, value, disabled, maxDate } = props

    const [fieldError, setFieldError] = useState<Partial<FormikDateFieldType>>({})
    const [date, setDate] = useState<FormikDateFieldType>(format.inputDate(value))
    const maxYear = maxDate ? maxDate?.getFullYear() : new Date().getFullYear()

    useEffect(() => setDate(format.inputDate(value)), [value])

    const handleDateChange = (field: DateFieldNames) => {
        return (event: ChangeEvent<HTMLInputElement>) => {
            const newDate = { ...date, [field]: event.target.value }
            setDate(newDate)

            const result = dateValidationSchema.safeParse(newDate)

            if (result.success) {
                setFieldError((prev) => ({ ...prev, [field]: undefined }))
                void setFieldValue(name, format.outputDate(result.data))
            } else {
                const error = result.error.issues.find((err) => err.path[0] === field)
                setFieldError((prev) => ({ ...prev, [field]: error?.message }))
            }
        }
    }

    const handleDateComplete = (
        nextElementRef: RefObject<HTMLInputElement | null>,
        field: DateFieldNames
    ) => {
        return (event: ChangeEvent<HTMLInputElement>) => {
            const value = event.target.value
            const result = dateValidationSchema.shape[field].safeParse(event.target.value)

            if (value.length >= 2) {
                if (result.success) {
                    nextElementRef.current?.focus()
                } else {
                    const error = result.error.issues[0]
                    setFieldError((prev) => ({ ...prev, [field]: error?.message }))
                }
            }
        }
    }

    const handleFocus = (event: ChangeEvent<HTMLInputElement>) => {
        event.target.select()
    }

    const getFieldName = (fieldName: DateFieldNames) => `${name}.${fieldName}`

    return (
        <Stack
            gap="xs"
            flexDirection="column"
        >
            <Grid
                gridTemplateColumns="repeat(12, 1fr)"
                gap="md"
            >
                <GridItem gridColumn={"span 4"}>
                    <TextField
                        type="number"
                        label={t("day")}
                        value={date.day}
                        disabled={disabled}
                        name={getFieldName(DateField.Day)}
                        onBlur={handleDateChange(DateField.Day)}
                        onChange={handleDateComplete(monthRef, DateField.Day)}
                        onFocus={handleFocus}
                        errorMessage={fieldError.day}
                        error={!!fieldError.day}
                        min={1}
                        max={31}
                    />
                </GridItem>
                <GridItem gridColumn={"span 4"}>
                    <TextField
                        ref={monthRef}
                        type="number"
                        label={t("month")}
                        value={date.month}
                        disabled={disabled}
                        name={getFieldName(DateField.Month)}
                        onBlur={handleDateChange(DateField.Month)}
                        onChange={handleDateComplete(yearRef, DateField.Month)}
                        onFocus={handleFocus}
                        errorMessage={fieldError.month}
                        error={!!fieldError.month}
                        min={1}
                        max={12}
                    />
                </GridItem>
                <GridItem gridColumn={"span 4"}>
                    <TextField
                        ref={yearRef}
                        type="number"
                        label={t("year")}
                        value={date.year}
                        disabled={disabled}
                        name={getFieldName(DateField.Year)}
                        onBlur={handleDateChange(DateField.Year)}
                        onFocus={handleFocus}
                        errorMessage={fieldError.year}
                        error={!!fieldError.year}
                        min={maxYear - 100}
                        max={maxYear}
                    />
                </GridItem>
            </Grid>
            {submitCount > 0 && <FormikErrorMessage field={name} />}
        </Stack>
    )
}

FormikDateField.messages = ["FormikDateField"] as const
